import { Close } from '@mui/icons-material';
import PestControlIcon from '@mui/icons-material/PestControl';
import { Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import settings from '/@/core/settings';
import { useCreateProblemReport, useGetProblemReportCategories } from '/@/hooks/help-center';
import { _ } from '/@/locale';
import { MuiSelect } from '/@/mui/select';
import ModalV2 from '../../bayer/modal-v2';
import FileDropperV2 from '../../file-dropper/v2';

type BugReportingFormType = {
    category: string | null;
    description: string | null;
};

const DESCRIPTION_MAX = 256;

const BugReporting = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
    const form = useForm<BugReportingFormType>({
        mode: 'onChange',
        defaultValues: {
            category: null,
            description: null,
        },
    });
    const {
        formState: { isValid, errors },
    } = form;
    const descriptionErrorMessage = errors.description?.message;
    const rawDescription = form.watch('description') ?? '';
    const descriptionLen = rawDescription.length;
    const remaining = DESCRIPTION_MAX - descriptionLen;
    const categoriesQuery = useGetProblemReportCategories();
    const { trigger: createProblemReport, isMutating: isCreatingProblemReport } = useCreateProblemReport();

    const requiredExtensions = useMemo(() => [['.jpg'], ['.jpeg'], ['.png'], ['.gif'], ['.pdf']], []);

    const [files, setFiles] = useState<File[]>([]);
    const [filesIsValid, setFilesIsValid] = useState<boolean>(false);

    let descriptionHint: string;
    if (descriptionLen > DESCRIPTION_MAX) {
        descriptionHint = _('Superó el límite');
    } else if (descriptionLen === DESCRIPTION_MAX) {
        descriptionHint = _('Usó exactamente 256 caracteres');
    } else if (remaining <= 20) {
        descriptionHint = _('{{count}} carácter disponible', { count: remaining });
    } else {
        descriptionHint = _('{{count}}/256 caracteres', { count: descriptionLen });
    }

    const descriptionCaptionColor =
        descriptionErrorMessage || descriptionLen > DESCRIPTION_MAX
            ? 'error'
            : descriptionLen === DESCRIPTION_MAX || remaining <= 20
              ? 'warning'
              : 'primary.mui.inactive';
    return (
        <ModalV2
            open={open}
            onClose={onClose}
            header={
                <Stack
                    direction='row'
                    alignItems='center'
                    justifyContent='space-between'
                    width='100%'
                    sx={{ px: 2, pt: 2 }}
                >
                    <Stack direction='row' alignItems='center' spacing={1}>
                        <PestControlIcon sx={{ fontSize: 24 }} />
                        <Typography
                            color='primary'
                            variant='h6'
                            sx={{
                                textAlign: 'center',
                            }}
                        >
                            {_('Reportar un problema')}
                        </Typography>
                    </Stack>
                    <IconButton
                        size={'large'}
                        onClick={onClose}
                        sx={{
                            m: 1,
                        }}
                    >
                        <Close fontSize='large' />
                    </IconButton>
                </Stack>
            }
            actions={[
                <Button
                    key='send-report'
                    fullWidth
                    variant='contained'
                    color='secondary'
                    disabled={!isValid || isCreatingProblemReport}
                    onClick={() => {
                        form.handleSubmit(async (data: BugReportingFormType) => {
                            await createProblemReport({
                                categoryId: Number(data.category),
                                description: (data.description ?? '').trim(),
                                ...(files.length > 0 ? { files } : {}),
                            });
                            onClose();
                            form.reset();
                            setFiles([]);
                            setFilesIsValid(false);
                        })();
                    }}
                >
                    {isCreatingProblemReport ? _('Enviando reporte...') : _('Enviar reporte')}
                </Button>,
            ]}
        >
            <Stack spacing={3} alignItems='center'>
                <Stack direction='column' textAlign='start' spacing={1}>
                    <Typography variant='h6' color='primary' maxWidth='800px'>
                        {_('¿Cómo podemos mejorar?')}
                    </Typography>
                    <Typography variant='body1' color='primary.mui.inactive' maxWidth='800px'>
                        {_(
                            'Con tus comentarios o sugerencias, nos estás ayudando a hacer de {{appName}} una mejor experiencia.',
                            { appName: settings.app.name }
                        )}
                    </Typography>
                </Stack>
                <Stack direction='row' spacing={4} width='100%'>
                    <MuiSelect
                        formControlProps={{ fullWidth: true }}
                        disabled={isCreatingProblemReport}
                        variant='outlined'
                        sx={{
                            width: '100%',
                            '& .MuiSelect-select': {
                                textAlign: 'start',
                            },
                        }}
                        inputLabel={_('Categoría')}
                        placeholder={_('Seleccioná una categoría')}
                        {...form.register('category', { required: _('La categoría es requerida') })}
                        options={(categoriesQuery.data?.results ?? []).map((category) => ({
                            value: String(category.id),
                            label: _(category.title),
                        }))}
                    />
                </Stack>
                <Stack direction='column' spacing={1} width='100%'>
                    <TextField
                        id='filled-multiline-static'
                        label={_('Descripción')}
                        multiline
                        fullWidth
                        disabled={isCreatingProblemReport}
                        rows={4}
                        variant='outlined'
                        {...form.register('description', {
                            validate: (value) => {
                                const v = value ?? '';
                                if (v.trim().length === 0) {
                                    return _('La descripción es requerida');
                                }
                                if (v.length > DESCRIPTION_MAX) {
                                    return _('Superó el límite');
                                }
                                return true;
                            },
                        })}
                        error={!!errors.description}
                    />
                    <Typography variant='caption' color={descriptionCaptionColor} textAlign='right'>
                        {descriptionErrorMessage ?? descriptionHint}
                    </Typography>
                </Stack>
                <Stack direction='column' alignItems='start' textAlign='start' spacing={1} width='100%'>
                    <Typography variant='subtitle1' color='primary' fontWeight='bold'>
                        {_('Adjuntar archivos')}
                    </Typography>

                    <FileDropperV2
                        requiredExtensions={requiredExtensions}
                        maxFiles={3}
                        files={files}
                        setFiles={setFiles}
                        onValidFiles={(validFiles) => {
                            if (validFiles.length) {
                                setFilesIsValid(true);
                            } else setFilesIsValid(false);
                        }}
                        text={_('Seleccionar archivos')}
                        disabled={isCreatingProblemReport}
                    />
                </Stack>
            </Stack>
        </ModalV2>
    );
};

export default BugReporting;
